import {
  CloseButton,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Table,
  Spinner,
  Center,
  Alert,
  SimpleGrid,
  Link,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { FiBriefcase, FiMapPin, FiPhone, FiMail, FiUser } from 'react-icons/fi'
import Map, { Marker } from 'react-map-gl/maplibre'
import { useWorksiteDetail } from '../../hooks/useBusiness'
import { getMapStyle } from '../../lib/mapConfig'
import 'maplibre-gl/dist/maplibre-gl.css'

interface BusinessDetailModalProps {
  worksiteId: string
  isOpen: boolean
  onClose: () => void
  onEmployeeClick?: (contactId: string) => void
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">{label}</Text>
      <Text fontSize="sm">{value}</Text>
    </Box>
  )
}

export function BusinessDetailModal({ worksiteId, isOpen, onClose, onEmployeeClick }: BusinessDetailModalProps) {
  const { data: worksite, isLoading, error } = useWorksiteDetail(worksiteId)

  const addressLine = worksite
    ? [worksite.streetAddress, worksite.city, worksite.state, worksite.zip].filter(Boolean).join(', ')
    : ''

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header>Worksite Details</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body pb={6}>
              {isLoading && (
                <Center py={20}>
                  <Spinner size="xl" color="accent" />
                </Center>
              )}

              {!!error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Worksite not found or an error occurred.
                </Alert.Root>
              )}

              {!isLoading && worksite && (
                <VStack gap={6} align="stretch">
                  {/* Worksite card header */}
                  <Box bg="surface.2" p={6} borderRadius="lg" border="1px" borderColor="gray.200">
                    <HStack gap={4} mb={4}>
                      <Box
                        w={12} h={12} bg="accent.soft" borderRadius="lg"
                        display="flex" alignItems="center" justifyContent="center"
                      >
                        <Box boxSize={6} color="accent" asChild><FiBriefcase /></Box>
                      </Box>
                      <Box flex={1}>
                        <HStack gap={2}>
                          <Text fontSize="xl" fontWeight="bold">{worksite.label}</Text>
                          {worksite.type && <Badge bg="info.subtle" color="info.fg">{worksite.type}</Badge>}
                          {!worksite.isActive && <Badge colorPalette="red">Inactive</Badge>}
                        </HStack>
                        {worksite.businessName && (
                          <Text fontSize="sm" color="gray.500">Business: {worksite.businessName}</Text>
                        )}
                        {worksite.description && (
                          <Text fontSize="sm" color="gray.600" mt={1}>{worksite.description}</Text>
                        )}
                      </Box>
                    </HStack>

                    <Separator mb={4} />

                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                      {addressLine && (
                        <HStack align="flex-start" gap={2}>
                          <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMapPin /></Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">Address</Text>
                            <Text fontSize="sm">{addressLine}</Text>
                          </Box>
                        </HStack>
                      )}
                      {worksite.phone && (
                        <HStack align="flex-start" gap={2}>
                          <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiPhone /></Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">Phone</Text>
                            <Text fontSize="sm">{worksite.phone}</Text>
                          </Box>
                        </HStack>
                      )}
                      {worksite.email && (
                        <HStack align="flex-start" gap={2}>
                          <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMail /></Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">Email</Text>
                            <Text fontSize="sm">{worksite.email}</Text>
                          </Box>
                        </HStack>
                      )}
                      {worksite.poc && (
                        <HStack align="flex-start" gap={2}>
                          <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiUser /></Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" fontWeight="medium">Point of Contact</Text>
                            <Text fontSize="sm">{worksite.poc}</Text>
                            {worksite.pocPhone && <Text fontSize="xs" color="gray.500">{worksite.pocPhone}</Text>}
                            {worksite.pocEmail && <Text fontSize="xs" color="gray.500">{worksite.pocEmail}</Text>}
                          </Box>
                        </HStack>
                      )}
                      <InfoField label="County" value={worksite.county} />
                      <InfoField label="Address Type" value={worksite.addressType} />
                    </SimpleGrid>
                  </Box>

                  {/* Location mini-map */}
                  {worksite.latitude != null && worksite.longitude != null && (
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" mb={2}>Location</Text>
                      <Box borderRadius="md" overflow="hidden" h="200px">
                        <Map
                          initialViewState={{
                            longitude: worksite.longitude,
                            latitude: worksite.latitude,
                            zoom: 15,
                          }}
                          style={{ width: '100%', height: '100%' }}
                          mapStyle={getMapStyle()}
                          attributionControl={false}
                        >
                          <Marker
                            longitude={worksite.longitude}
                            latitude={worksite.latitude}
                            color="#DD6B20"
                          />
                        </Map>
                      </Box>
                    </Box>
                  )}

                  {/* Employees section */}
                  <Box>
                    <Text fontSize="md" fontWeight="semibold" mb={2}>
                      Employees ({worksite.employees.length})
                    </Text>
                    {worksite.employees.length === 0 ? (
                      <Text color="gray.500" fontSize="sm">No employees linked to this worksite yet.</Text>
                    ) : (
                      <Table.Root size="sm">
                        <Table.Header bg="gray.50">
                          <Table.Row>
                            <Table.ColumnHeader>Name</Table.ColumnHeader>
                            <Table.ColumnHeader>Position</Table.ColumnHeader>
                            <Table.ColumnHeader>Department</Table.ColumnHeader>
                            <Table.ColumnHeader>Primary</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {worksite.employees.map(emp => (
                            <Table.Row
                              key={emp.employmentId}
                              _hover={{ bg: 'gray.50', cursor: onEmployeeClick ? 'pointer' : 'default' }}
                              onClick={() => onEmployeeClick?.(emp.contactId)}
                            >
                              <Table.Cell>
                                <Link
                                  color="accent"
                                  fontWeight="medium"
                                  fontSize="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onEmployeeClick?.(emp.contactId)
                                  }}
                                >
                                  {emp.contactName}
                                </Link>
                              </Table.Cell>
                              <Table.Cell><Text fontSize="sm">{emp.position || '---'}</Text></Table.Cell>
                              <Table.Cell><Text fontSize="sm">{emp.department || '---'}</Text></Table.Cell>
                              <Table.Cell>
                                {emp.isPrimary && <Badge colorPalette="green" fontSize="xs">Primary</Badge>}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    )}
                  </Box>

                  {/* Union affiliations */}
                  {worksite.unionAffiliations.length > 0 && (
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" mb={2}>
                        Union Affiliations ({worksite.unionAffiliations.length})
                      </Text>
                      <Table.Root size="sm">
                        <Table.Header bg="gray.50">
                          <Table.Row>
                            <Table.ColumnHeader>Union</Table.ColumnHeader>
                            <Table.ColumnHeader>Local #</Table.ColumnHeader>
                            <Table.ColumnHeader>Contract Start</Table.ColumnHeader>
                            <Table.ColumnHeader>Contract End</Table.ColumnHeader>
                            <Table.ColumnHeader>Status</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {worksite.unionAffiliations.map(ua => (
                            <Table.Row key={ua.worksiteUnionId}>
                              <Table.Cell><Text fontWeight="medium" fontSize="sm">{ua.unionName}</Text></Table.Cell>
                              <Table.Cell><Text fontSize="sm">{ua.localNumber || '---'}</Text></Table.Cell>
                              <Table.Cell><Text fontSize="sm">{ua.contractStartDate ? new Date(ua.contractStartDate).toLocaleDateString() : '---'}</Text></Table.Cell>
                              <Table.Cell><Text fontSize="sm">{ua.contractEndDate ? new Date(ua.contractEndDate).toLocaleDateString() : '---'}</Text></Table.Cell>
                              <Table.Cell>
                                <Badge colorPalette={ua.isActive ? 'green' : 'gray'} fontSize="xs">
                                  {ua.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}
                </VStack>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
